import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Skeleton, useToast } from '../../../design-system';
import '../../styles/Finance.css';

// Who at this client receives invoices.
//
// Suggestions are re-derived server-side on every load from the client's
// contacts and portal logins, rather than snapshotted — so a contact who
// changes their email shows up as a NEW address to tick, instead of silently
// redirecting the firm's invoices to wherever that person moved.
//
// Ticking nothing is a valid, and common, choice: the resolver then falls back
// to the primary contact and then to the portal logins. The card says which of
// those is in force so nobody has to guess where invoices are actually going.

function BillingRecipients({ clientId, canWrite = true }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [manual, setManual] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    apiClient.get(`/api/finance/clients/${clientId}/billing-recipients`)
      .then((r) => { setData(r.data); setSelected(r.data.selected || []); })
      .catch((e) => toast.danger(e.response?.data?.error || 'Could not load billing contacts'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);
  useEffect(load, [load]);

  const toggle = (email) => setSelected((s) => (
    s.includes(email) ? s.filter((x) => x !== email) : [...s, email]
  ));

  const addManual = () => {
    const email = manual.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.danger('That does not look like an email address');
      return;
    }
    if (!selected.includes(email)) setSelected((s) => [...s, email]);
    setManual('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiClient.put(`/api/finance/clients/${clientId}/billing-recipients`, { emails: selected });
      setData((d) => ({ ...d, selected: r.data.selected, effective: r.data.effective }));
      toast.success('Billing contacts saved');
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <Card><Skeleton.Text lines={4} /></Card>;

  // Anything ticked that isn't in the suggestion list (typed by hand this session).
  const extra = selected.filter((e) => !(data.suggestions || []).some((s) => s.email === e));
  const dirty = JSON.stringify([...selected].sort()) !== JSON.stringify([...(data.selected || [])].sort());

  return (
    <Card>
      <h3 className="fin-h">Billing email</h3>
      <p className="fin-sub">
        Who gets this client's proformas, tax invoices and receipts. Leave everything unticked to
        fall back to the primary contact.
      </p>

      <div className="fin-recip-list">
        {(data.suggestions || []).map((s) => (
          <label key={s.email} className="fin-recip">
            <input
              type="checkbox"
              checked={selected.includes(s.email)}
              onChange={() => toggle(s.email)}
              disabled={!canWrite}
            />
            <span className="fin-recip-main">
              {s.email}
              <span className="fin-muted"> · {s.label} ({s.via})</span>
            </span>
          </label>
        ))}
        {extra.map((email) => (
          <label key={email} className="fin-recip">
            <input type="checkbox" checked onChange={() => toggle(email)} disabled={!canWrite} />
            <span className="fin-recip-main">{email}<span className="fin-muted"> · just added</span></span>
          </label>
        ))}
        {!(data.suggestions || []).length && !extra.length && (
          <p className="fin-sub" style={{ margin: 0 }}>
            This client has no contact email and no portal login. Add one below, or invoices
            cannot be emailed.
          </p>
        )}
      </div>

      {canWrite && (
        <div className="fin-form-row" style={{ alignItems: 'flex-end' }}>
          <Input
            label="Add another address"
            placeholder="accounts@client.com"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addManual(); } }}
          />
          <Button variant="secondary" onClick={addManual}>Add</Button>
        </div>
      )}

      <p className="fin-sub" style={{ marginBottom: canWrite ? 8 : 0 }}>
        {data.effective?.to?.length
          ? <>Invoices currently go to <b>{data.effective.to.join(', ')}</b> — {data.effective.label}.</>
          : <span className="fin-bad">No email on file — invoices for this client cannot be sent.</span>}
      </p>

      {canWrite && (
        <Button variant="primary" onClick={save} loading={saving} disabled={!dirty}>
          Save billing contacts
        </Button>
      )}
    </Card>
  );
}

export default BillingRecipients;
